import React, { useState } from 'react';
import { Modal, List, Button, Typography, Space, Tag, Divider, Empty, Tooltip, message } from 'antd';
import { 
  FileTextOutlined, 
  FilePdfOutlined, 
  FileImageOutlined, 
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileOutlined,
  DownloadOutlined,
  EyeOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { Asset } from '../../../../types/asset.types';
import FileService from '../../../../services/file/file.service';

const { Title, Text } = Typography;

interface ViewFilesModalProps {
  visible: boolean;
  onClose: () => void;
  asset: Asset | null;
}

const ViewFilesModal: React.FC<ViewFilesModalProps> = ({
  visible,
  onClose,
  asset
}) => {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  // Modal kapandığında blob URL'lerini temizle
  React.useEffect(() => {
    if (!visible) {
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
        setPreviewUrl('');
      }
      setPreviewVisible(false);
      setPreviewFile(null);
    }
  }, [visible]);

  if (!asset) return null;

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />;
      case 'doc':
      case 'docx':
        return <FileWordOutlined style={{ color: '#1890ff', fontSize: '20px' }} />;
      case 'xls':
      case 'xlsx':
        return <FileExcelOutlined style={{ color: '#52c41a', fontSize: '20px' }} />;
      case 'ppt':
      case 'pptx':
        return <FilePptOutlined style={{ color: '#fa8c16', fontSize: '20px' }} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'webp':
        return <FileImageOutlined style={{ color: '#722ed1', fontSize: '20px' }} />;
      case 'txt':
        return <FileTextOutlined style={{ color: '#8c8c8c', fontSize: '20px' }} />;
      default:
        return <FileOutlined style={{ color: '#8c8c8c', fontSize: '20px' }} />;
    }
  };

  const getFileSize = (sizeInBytes: number) => {
    if (sizeInBytes < 1024) return `${sizeInBytes} B`;
    if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImageFile = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension || '');
  };

  const handleDownload = async (file: any) => {
    try {
      const fileName = file.fileName || file.name || 'download';
      const blob = await FileService.getInstance().getFileViewUrl(file.path);
      
      // Blob boşsa veya hatalıysa
      if (!blob || blob.size === 0) {
        message.error('Dosya bulunamadı');
        return;
      }
      
      // Blob'dan Object URL oluştur
      const url = window.URL.createObjectURL(blob);
      
      // Link oluştur ve indir
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Object URL'i temizle
      window.URL.revokeObjectURL(url);
      
      message.success('Dosya indiriliyor');
    } catch (error: any) {
      if (error?.response?.status === 404) {
        message.error('Dosya bulunamadı');
      } else if (error?.message?.includes('404')) {
        message.error('Dosya bulunamadı');
      } else {
        message.error('Dosya indirilirken hata oluştu');
      }
    }
  };

  const handlePreview = async (file: any) => {
    try {
      const blob = await FileService.getInstance().getFileViewUrl(file.path);
      
      // Blob boşsa veya hatalıysa
      if (!blob || blob.size === 0) {
        message.error('Dosya bulunamadı');
        return;
      }
      
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewFile(file);
      
      setPreviewVisible(true);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        message.error('Dosya bulunamadı');
      } else if (error?.message?.includes('404')) {
        message.error('Dosya bulunamadı');
      } else {
        message.error('Dosya görüntülenirken hata oluştu');
      }
    }
  };

  const handleClosePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    }
    setPreviewVisible(false);
    setPreviewFile(null);
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <FileTextOutlined style={{ color: 'white', fontSize: '18px' }} />
          </div>
          <div>
            <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
              Dosyalar
            </Title>
            <Text type="secondary">
              {asset.name} - {asset.serialNumber}
            </Text>
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose} icon={<CloseOutlined />}>
          Kapat
        </Button>
      ]}
      width={800}
      style={{ top: 20 }}
      bodyStyle={{ 
        maxHeight: '70vh', 
        overflowY: 'auto',
        padding: '20px'
      }}
    >
      <div style={{
        background: 'linear-gradient(135deg, #f6f9fc 0%, #e9f4ff 100%)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
              📎 {asset.files?.length || 0} Dosya
            </Text>
            <div style={{ marginTop: '4px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Bu zimmete ait tüm dosyalar
              </Text>
            </div>
          </div>
          <Tag color="blue" style={{ fontSize: '12px', padding: '4px 12px' }}>
            {asset.files?.length || 0} adet
          </Tag>
        </div>
      </div>

      {!asset.files || asset.files.length === 0 ? (
        <Empty
          image="/assets/img/icons/empty-files.svg"
          imageStyle={{ height: 120 }}
          description={
            <div>
              <Text type="secondary" style={{ fontSize: '16px' }}>
                Henüz dosya yüklenmemiş
              </Text>
              <div style={{ marginTop: '8px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Bu zimmete ait herhangi bir dosya bulunmuyor
                </Text>
              </div>
            </div>
          }
        />
      ) : (
        <List
          dataSource={asset.files}
          renderItem={(file: any, index: number) => (
            <List.Item
              style={{
                background: 'white',
                borderRadius: '12px',
                marginBottom: '12px',
                border: '1px solid #f0f0f0',
                padding: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <div style={{ marginRight: '16px' }}>
                  {getFileIcon(file.path || file.name)}
                </div>
                
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                    <Text strong style={{ 
                      fontSize: '14px',
                      color: '#262626',
                      marginRight: '8px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '300px'
                    }}>
                      {file.fileName.split('.')[0] || file.name}
                    </Text>
                    <Tag 
                      color="default" 
                      style={{ 
                        fontSize: '10px', 
                        padding: '2px 6px',
                        borderRadius: '4px'
                      }}
                    >
                      {file.fileName?.split('.').pop()?.toUpperCase() || 'FILE'}
                    </Tag>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      📏 {getFileSize(file.size || 0)}
                    </Text>
                    {file.uploadedAt && (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        📅 {new Date(file.uploadedAt).toLocaleDateString('tr-TR')}
                      </Text>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                  <Tooltip title="Görüntüle">
                    <Button
                      type="text"
                      size="middle"
                      icon={<EyeOutlined />}
                      onClick={() => handlePreview(file)}
                      style={{
                        color: '#1890ff',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="İndir">
                    <Button
                      type="text"
                      size="middle"
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownload(file)}
                      style={{
                        color: '#52c41a',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    />
                  </Tooltip>
                </div>
              </div>
            </List.Item>
          )}
        />
      )}

      {/* Önizleme Modal'ı */}
      <Modal
        open={previewVisible}
        onCancel={handleClosePreview}
        footer={[
          <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={() => previewFile && handleDownload(previewFile)}>
            İndir
          </Button>,
          <Button key="close" onClick={handleClosePreview}>
            Kapat
          </Button>
        ]}
        width={800}
        centered
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <EyeOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
            <Text strong>{previewFile?.fileName || 'Dosya Önizleme'}</Text>
          </div>
        }
      >
        {previewFile && previewUrl && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            width: '100%',
            minHeight: '400px',
            maxHeight: '70vh',
            background: '#f5f5f5',
            borderRadius: '8px',
            padding: '20px',
            overflow: 'auto'
          }}>
            {isImageFile(previewFile.path || previewFile.name || previewFile.fileName) ? (
              <img 
                src={previewUrl} 
                alt={previewFile.fileName} 
                style={{ 
                  maxWidth: '100%',
                  maxHeight: '65vh',
                  height: 'auto',
                  width: 'auto',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  display: 'block',
                  margin: '0 auto'
                }} 
              />
            ) : (
              <iframe
                src={previewUrl}
                style={{
                  width: '100%',
                  height: '65vh',
                  border: 'none',
                  borderRadius: '4px'
                }}
                title={previewFile.fileName}
              />
            )}
          </div>
        )}
      </Modal>
    </Modal>
  );
};

export default ViewFilesModal;